import React from "react";
import { useTranslation } from "react-i18next";
import { FiTrendingUp, FiTrendingDown } from "react-icons/fi";

/**
 * Check whether metrics is an array (text-style) or object (metric-style).
 *
 * Text-style (communication):
 *   [{ label, current_value, previous_value }]
 *
 * Metric-style (usage_pattern, feature_usage):
 *   { "DAU": { "Feb": 2, "Mar": 1 }, "WAU": { ... } }
 */
const isMetricStyle = (metrics) => metrics && !Array.isArray(metrics);

/**
 * Flatten metric-style dict into an array the card can render.
 *
 * { "DAU": { "Feb": 2, "Mar": 1 } }
 *   → [{ label: "DAU", current_value: 1, previous_value: 2 }]
 */
const normalizeMetrics = (metrics) => {
  if (!metrics) return [];
  if (Array.isArray(metrics)) return metrics;

  return Object.entries(metrics).map(([label, monthData]) => {
    const months = Object.keys(monthData);
    const currentMonth = months[months.length - 1];
    const previousMonth = months.length > 1 ? months[months.length - 2] : null;

    return {
      label,
      current_value: monthData[currentMonth],
      previous_value: previousMonth ? monthData[previousMonth] : null,
      current_month: currentMonth,
      previous_month: previousMonth,
    };
  });
};

export const SignalCard = ({ category, icon: Icon, data }) => {
  const { t } = useTranslation();

  const { summary, metrics, trend_percent, is_positive_good } = data;
  const normalized = normalizeMetrics(metrics);
  const isMonthKeyed = isMetricStyle(metrics);

  // Logic to determine if a trend is "bad" based on your data schema
  // e.g., an increase in tickets (trend > 0) is bad (is_positive_good: false)
  const isTrendBad =
    (trend_percent > 0 && !is_positive_good) ||
    (trend_percent < 0 && is_positive_good);
  const trendColor = isTrendBad ? "text-red-500" : "text-emerald-600";

  return (
    <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-colors hover:border-slate-300">
      <div className="mb-4 flex items-center gap-2 text-slate-400">
        <Icon size={18} />
        <span className="text-xs font-semibold uppercase tracking-widest">
          {t(`pages.customer.accountStates.categories.${category}`, category)}
        </span>
      </div>

      <div className="flex-grow">
        {isMonthKeyed ? (
          /* Metric-style: show each metric (DAU/WAU/MAU) with current → previous */
          <div className="space-y-2">
            {normalized.map((m) => (
              <div key={m.label} className="flex justify-between text-sm">
                <span className="font-medium text-slate-700">{m.label}</span>
                <span className="text-slate-600">
                  {m.current_value?.toLocaleString() ?? "—"}
                  {m.previous_value != null && (
                    <span className="ml-1 text-slate-400">
                      (was {m.previous_value.toLocaleString()})
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        ) : (
          /* Text-style: label + current/previous rows */
          <>
            <h3 className="mb-2 text-lg font-bold text-slate-800">
              {normalized[0]?.label || "—"}
            </h3>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Current</span>
                <span className="font-medium text-slate-700">
                  {normalized[0]?.current_value ?? "—"}
                </span>
              </div>
              {normalized[0]?.previous_value != null && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Previous</span>
                  <span className="text-slate-500">
                    {normalized[0]?.previous_value}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="mt-6">
        {trend_percent !== 0 && (
          <div
            className={`mb-2 flex items-center gap-1 text-sm font-bold ${trendColor}`}
          >
            {trend_percent > 0 ? <FiTrendingUp /> : <FiTrendingDown />}
            {Math.abs(trend_percent)}%{" "}
            {trend_percent > 0 ? "increase" : "decline"}
          </div>
        )}

        <p className="text-sm italic leading-relaxed text-slate-500">
          {summary}
        </p>
      </div>
    </div>
  );
};
